import { UploadOutlined } from "@ant-design/icons";
import { App, Button, Checkbox, Input, InputNumber, Modal, Select, Table, Upload } from "antd";
import type { AxiosError } from "axios";
import { Fragment, useMemo, useState } from "react";
import { Circle, CircleMarker, MapContainer, Polygon, TileLayer } from "react-leaflet";
import { FitBounds } from "@/shared/map/FitBounds";
import { geoJsonToLeafletPositions, geometryToLatLngs } from "@/shared/map/geojson";
import {
	useConfirmImportTerritory,
	usePreviewImportTerritory,
	useTerritories,
} from "../hooks/useTerritories";
import type { KmzPlacemark } from "../services/territory.service";
import { useTerritoriesStore } from "../store/territories.store";

interface TerritoryImportModalProps {
	organizationId: string;
}

interface SelectionRow {
	index: number;
	name: string;
	checked: boolean;
	isPoint: boolean;
	/** Buffer radius (m) for point placemarks; null while the input is cleared. */
	radius: number | null;
}

const SELECTED_COLOR = "#1677ff";
const UNSELECTED_COLOR = "#9ca3af";
const DEFAULT_POINT_RADIUS_M = 50;

function extractApiErrorMessage(err: unknown, fallback: string): string {
	const axiosErr = err as AxiosError<{ error?: { message?: string } }>;
	return axiosErr.response?.data?.error?.message ?? fallback;
}

export function TerritoryImportModal({ organizationId }: TerritoryImportModalProps) {
	const { message } = App.useApp();
	const { importModalOpen, closeImportModal } = useTerritoriesStore();
	const preview = usePreviewImportTerritory();
	const confirm = useConfirmImportTerritory();
	const { data: territoriesData } = useTerritories({ organizationId, page: 1, limit: 100 });

	const [file, setFile] = useState<File | null>(null);
	const [placemarks, setPlacemarks] = useState<KmzPlacemark[]>([]);
	const [skipped, setSkipped] = useState(0);
	const [rows, setRows] = useState<SelectionRow[]>([]);
	const [parentId, setParentId] = useState<string | undefined>(undefined);

	const step: "upload" | "select" = placemarks.length === 0 ? "upload" : "select";
	const existingTerritories = territoriesData?.data?.items ?? [];

	const allPositions = useMemo(
		() =>
			placemarks.flatMap((p) =>
				p.geometryType === "point"
					? [[p.lat, p.lng] as [number, number]]
					: geometryToLatLngs(p.geometry)
			),
		[placemarks]
	);
	const checkedCount = rows.filter((r) => r.checked).length;

	const reset = () => {
		setFile(null);
		setPlacemarks([]);
		setSkipped(0);
		setRows([]);
		setParentId(undefined);
	};

	const handleClose = () => {
		closeImportModal();
		reset();
	};

	const handlePreview = () => {
		if (!file) {
			return;
		}
		preview.mutate(
			{ file, organizationId },
			{
				onSuccess: (data) => {
					setPlacemarks(data.placemarks);
					setSkipped(data.skipped);
					setRows(
						data.placemarks.map((p) => ({
							index: p.index,
							name: p.name ?? `Hudud ${p.index + 1}`,
							checked: true,
							isPoint: p.geometryType === "point",
							radius: p.geometryType === "point" ? DEFAULT_POINT_RADIUS_M : null,
						}))
					);
				},
				onError: (err) => {
					message.error(extractApiErrorMessage(err, "KMZ faylni o'qib bo'lmadi"));
				},
			}
		);
	};

	const handleConfirm = () => {
		if (!file) {
			return;
		}
		const selections = rows
			.filter((r) => r.checked)
			.map((r) => ({
				index: r.index,
				name: r.name.trim() || `Hudud ${r.index + 1}`,
				parentId,
				bufferRadiusM: r.isPoint ? (r.radius ?? DEFAULT_POINT_RADIUS_M) : undefined,
			}));
		if (selections.length === 0) {
			return;
		}
		confirm.mutate(
			{ file, organizationId, selections },
			{
				onSuccess: handleClose,
				onError: (err) => {
					message.error(extractApiErrorMessage(err, "Import qilishda xatolik yuz berdi"));
				},
			}
		);
	};

	const toggleRow = (index: number, checked: boolean) => {
		setRows((prev) => prev.map((r) => (r.index === index ? { ...r, checked } : r)));
	};

	const renameRow = (index: number, name: string) => {
		setRows((prev) => prev.map((r) => (r.index === index ? { ...r, name } : r)));
	};

	const setRowRadius = (index: number, radius: number | null) => {
		setRows((prev) => prev.map((r) => (r.index === index ? { ...r, radius } : r)));
	};

	return (
		<Modal
			title={
				step === "upload"
					? "KMZ faylidan hudud import qilish"
					: "Import qilinadigan hududlarni tanlang"
			}
			open={importModalOpen}
			onCancel={handleClose}
			onOk={step === "upload" ? handlePreview : handleConfirm}
			okText={step === "upload" ? "Ko'rish" : `Import qilish (${checkedCount})`}
			okButtonProps={{
				disabled: step === "upload" ? !file : checkedCount === 0,
			}}
			confirmLoading={preview.isPending || confirm.isPending}
			cancelText="Bekor qilish"
			destroyOnHidden
			width={860}
		>
			{step === "upload" ? (
				<Upload.Dragger
					accept=".kmz"
					maxCount={1}
					beforeUpload={(f) => {
						setFile(f);
						return false;
					}}
					onRemove={() => setFile(null)}
				>
					<p className="ant-upload-drag-icon">
						<UploadOutlined />
					</p>
					<p className="ant-upload-text">KMZ faylni tanlang yoki shu yerga tashlang</p>
				</Upload.Dragger>
			) : (
				<div className="flex flex-col gap-4">
					<div style={{ height: 320 }}>
						<MapContainer
							style={{ height: "100%", width: "100%" }}
							center={[41.31, 69.28]}
							zoom={11}
						>
							<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
							<FitBounds positions={allPositions} fitKey={file?.name} />
							{placemarks.map((p) => (
								<PlacemarkLayer
									key={p.index}
									placemark={p}
									row={rows.find((r) => r.index === p.index)}
									onToggle={toggleRow}
								/>
							))}
						</MapContainer>
					</div>
					{skipped > 0 && (
						<div className="text-amber-600">
							{skipped} ta yaroqsiz obyekt o'tkazib yuborildi (chiziq yoki chegarasi xato)
						</div>
					)}
					<Select
						placeholder="Yuqori turuvchi hudud (ixtiyoriy, barchasiga qo'llanadi)"
						allowClear
						showSearch
						optionFilterProp="label"
						value={parentId}
						onChange={setParentId}
						options={existingTerritories.map((t) => ({ value: t.id, label: t.name }))}
					/>
					<Table<SelectionRow>
						size="small"
						rowKey="index"
						pagination={false}
						scroll={{ y: 240 }}
						dataSource={rows}
						columns={[
							{
								title: "",
								width: 48,
								render: (_, row) => (
									<Checkbox
										checked={row.checked}
										onChange={(e) => toggleRow(row.index, e.target.checked)}
									/>
								),
							},
							{
								title: "Hudud nomi",
								render: (_, row) => (
									<Input
										value={row.name}
										onChange={(e) => renameRow(row.index, e.target.value)}
										maxLength={255}
									/>
								),
							},
							{
								title: "Radius (m)",
								width: 130,
								render: (_, row) =>
									row.isPoint && row.checked ? (
										<InputNumber
											value={row.radius}
											onChange={(value) => setRowRadius(row.index, value)}
											min={10}
											max={1000}
											suffix="m"
											style={{ width: "100%" }}
										/>
									) : row.isPoint ? (
										"Nuqta"
									) : (
										"—"
									),
							},
							{
								title: "Maydon (m²)",
								width: 140,
								render: (_, row) => {
									const placemark = placemarks[row.index];
									return placemark &&
										(placemark.geometryType === "polygon" ||
											placemark.geometryType === "multipolygon")
										? placemark.area.toLocaleString()
										: "—";
								},
							},
						]}
					/>
					<Button type="link" className="self-start p-0" onClick={reset}>
						← Boshqa fayl tanlash
					</Button>
				</div>
			)}
		</Modal>
	);
}

interface PlacemarkLayerProps {
	placemark: KmzPlacemark;
	row: SelectionRow | undefined;
	onToggle: (index: number, checked: boolean) => void;
}

/** One preview layer: point → marker (+ buffer circle), polygon → outline. */
function PlacemarkLayer({ placemark, row, onToggle }: PlacemarkLayerProps) {
	const checked = row?.checked ?? false;
	const color = checked ? SELECTED_COLOR : UNSELECTED_COLOR;
	const toggle = () => onToggle(placemark.index, !checked);

	if (placemark.geometryType === "point") {
		return (
			<Fragment>
				{checked && (
					<Circle
						center={[placemark.lat, placemark.lng]}
						radius={row?.radius ?? DEFAULT_POINT_RADIUS_M}
						pathOptions={{ color, fillOpacity: 0.15 }}
					/>
				)}
				<CircleMarker
					center={[placemark.lat, placemark.lng]}
					radius={6}
					pathOptions={{ color, fillColor: color, fillOpacity: checked ? 0.9 : 0.4 }}
					eventHandlers={{ click: toggle }}
				/>
			</Fragment>
		);
	}

	return (
		<Polygon
			positions={geoJsonToLeafletPositions(placemark.geometry.coordinates) as any}
			pathOptions={{ color, fillOpacity: checked ? 0.3 : 0.1 }}
			eventHandlers={{ click: toggle }}
		/>
	);
}
